import type { ApiClientConfig, RequestOptions } from '@/types/api'
import { ApiError, NetworkError, TimeoutError } from '@/types/api'

class ApiClient {
  private baseURL: string
  private timeout: number
  private bearerToken?: string

  constructor(config: ApiClientConfig) {
    // VITE_API_URL is the full origin (e.g. https://api.rivulet.webby.sigler.io).
    // In dev, leave it empty so requests go through the Vite proxy.
    this.baseURL = config.baseURL || import.meta.env.VITE_API_URL || ''
    this.timeout = config.timeout || 30000 // 30 seconds default

    // Bearer tokens are only used for CLI / programmatic API clients.
    // Browser sessions authenticate via the OIDC session cookie.
    this.bearerToken = import.meta.env.VITE_API_TOKEN || localStorage.getItem('api_token') || undefined
  }

  /**
   * Set Bearer token for API authentication (CLI / programmatic clients)
   */
  setBearerToken(token: string): void {
    this.bearerToken = token
    localStorage.setItem('api_token', token)
  }

  /**
   * Clear Bearer token
   */
  clearBearerToken(): void {
    this.bearerToken = undefined
    localStorage.removeItem('api_token')
  }

  /**
   * Build query string from params object
   */
  private buildQueryString(
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    if (!params) return ''

    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })

    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }

  /**
   * Redirect the browser to the OIDC login endpoint, preserving the current
   * location so the user returns here after authentication.
   */
  private redirectToLogin(): void {
    const apiUrl = import.meta.env.VITE_API_URL || ''
    window.location.href = `${apiUrl}/oidc/login`
  }

  /**
   * Make HTTP request with timeout and error handling
   */
  private async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, timeout = this.timeout, ...fetchOptions } = options

    // Build full URL with query params
    const queryString = this.buildQueryString(params)
    const url = `${this.baseURL}${path}${queryString}`

    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      // Bearer token overrides cookie auth (for CLI / programmatic clients).
      // Otherwise the browser sends the OIDC session cookie automatically.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (this.bearerToken) {
        headers.Authorization = `Bearer ${this.bearerToken}`
      }
      if (fetchOptions.headers) {
        Object.assign(headers, fetchOptions.headers as Record<string, string>)
      }

      const response = await fetch(url, {
        ...fetchOptions,
        method,
        signal: controller.signal,
        credentials: 'include',
        headers,
      })

      clearTimeout(timeoutId)

      // Handle non-OK responses
      if (!response.ok) {
        // 401 on a cookie-auth request means the session has expired or was
        // never established — bounce the browser to the OIDC login endpoint.
        // Bearer-token clients get a normal ApiError so they can handle it.
        if (response.status === 401 && !this.bearerToken) {
          this.redirectToLogin()
          throw new ApiError('Session expired', 401)
        }

        const errorData = await response.json().catch(() => ({
          error: 'Unknown error',
          message: response.statusText,
        }))

        throw new ApiError(
          errorData.message || response.statusText,
          response.status,
          errorData.code,
          errorData.details
        )
      }

      // Handle empty responses (204 No Content, etc.)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T
      }

      return await response.json()
    } catch (error) {
      clearTimeout(timeoutId)

      // Handle timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${timeout}ms`)
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new NetworkError('Network request failed', error)
      }

      // Re-throw API errors
      if (error instanceof ApiError) {
        throw error
      }

      // Wrap unknown errors
      throw new NetworkError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        error instanceof Error ? error : undefined
      )
    }
  }

  /**
   * HTTP GET request
   */
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options)
  }

  /**
   * HTTP POST request
   */
  async post<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('POST', path, {
      ...options,
      body: JSON.stringify(body),
    })
  }

  /**
   * HTTP PUT request
   */
  async put<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PUT', path, {
      ...options,
      body: JSON.stringify(body),
    })
  }

  /**
   * HTTP PATCH request
   */
  async patch<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PATCH', path, {
      ...options,
      body: JSON.stringify(body),
    })
  }

  /**
   * HTTP DELETE request
   */
  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options)
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_API_URL,
})

export default apiClient
